/**
 * Unified registry over both local-page tiers:
 *   - "town"   → /locations/:stateSlug/:townSlug        (src/data/towns.ts)
 *   - "region" → /locations/:regionSlug                 (src/data/locations.ts)
 *
 * Both tiers store AI copy in `public.town_content` keyed by
 * (state_slug, town_slug, service_slug). For regions, `town_slug` is the
 * region slug (globally unique) and `state_slug` is the state's slug.
 */

import { locations, getLocationBySlug, type LocationData } from "./locations";
import { towns, getTown, type TownData } from "./towns";

export type LocationTier = "town" | "region";

export interface LocationTarget {
  tier: LocationTier;
  /** Town slug (unique within its state) or region slug (globally unique). */
  slug: string;
  name: string;
  /** Two-letter state abbreviation, e.g. "CT". */
  state: string;
  stateName: string;
  stateSlug: string;
  /** Public URL of the target's own page. */
  href: string;
}

export interface ContentState {
  slug: string;
  name: string;
  abbr: string;
}

/** Every state that has at least one town or region page. */
const STATE_BY_ABBR: Record<string, { slug: string; name: string }> = {
  CT: { slug: "connecticut", name: "Connecticut" },
  NY: { slug: "new-york", name: "New York" },
  MA: { slug: "massachusetts", name: "Massachusetts" },
  NJ: { slug: "new-jersey", name: "New Jersey" },
  PA: { slug: "pennsylvania", name: "Pennsylvania" },
  MD: { slug: "maryland", name: "Maryland" },
  VA: { slug: "virginia", name: "Virginia" },
  NC: { slug: "north-carolina", name: "North Carolina" },
  SC: { slug: "south-carolina", name: "South Carolina" },
  GA: { slug: "georgia", name: "Georgia" },
  TN: { slug: "tennessee", name: "Tennessee" },
  RI: { slug: "rhode-island", name: "Rhode Island" },
  VT: { slug: "vermont", name: "Vermont" },
  NH: { slug: "new-hampshire", name: "New Hampshire" },
};

export const stateForAbbr = (abbr: string) => STATE_BY_ABBR[abbr.toUpperCase()];

const regionTarget = (loc: LocationData): LocationTarget => {
  const st = stateForAbbr(loc.state);
  return {
    tier: "region",
    slug: loc.slug,
    name: loc.city,
    state: loc.state,
    stateName: st?.name ?? loc.state,
    stateSlug: st?.slug ?? loc.state.toLowerCase(),
    href: `/locations/${loc.slug}`,
  };
};

const townTarget = (town: TownData): LocationTarget => ({
  tier: "town",
  slug: town.slug,
  name: town.name,
  state: town.state,
  stateName: town.stateName,
  stateSlug: town.stateSlug,
  href: `/locations/${town.stateSlug}/${town.slug}`,
});

export const locationTargets: LocationTarget[] = [
  ...towns.map(townTarget),
  ...locations.map(regionTarget),
];

/** All states with local pages, alphabetical. */
export const contentStates: ContentState[] = Object.entries(
  locationTargets.reduce<Record<string, ContentState>>((acc, t) => {
    acc[t.stateSlug] = { slug: t.stateSlug, name: t.stateName, abbr: t.state };
    return acc;
  }, {}),
)
  .map(([, s]) => s)
  .sort((a, b) => a.name.localeCompare(b.name));

export const getTargetsInState = (stateSlug: string, tier?: LocationTier) =>
  locationTargets.filter((t) => t.stateSlug === stateSlug && (!tier || t.tier === tier));

/**
 * Resolve a (state_slug, town_slug) content key back to its target.
 * Towns are looked up within the state; regions by their globally unique slug.
 */
export function getLocationTarget(stateSlug: string, slug: string): LocationTarget | undefined {
  const town = getTown(stateSlug, slug);
  if (town) return townTarget(town);
  const region = getLocationBySlug(slug);
  if (region) return regionTarget(region);
  return undefined;
}

/** Is this slug a county/region page (as opposed to a state hub or town)? */
export const isRegionSlug = (slug: string) => Boolean(getLocationBySlug(slug));
